/**
 * What «Решить» asks of me, counted once (PR #18 review, point 1): the screen's
 * «N ждут тебя» and the tab's counter are the same number.
 */

import type { ActiveTask, OverviewOut, WaitingItem } from "@/api/types";

/** My tasks that are stuck: an obstacle in the journal, or a task they wait for. */
export function myBlocked(data: OverviewOut | undefined): ActiveTask[] {
  const me = data?.me.id;
  if (!me) return [];
  return data.active.filter((a) => a.assignee?.id === me && (a.flags.includes("blocked") || Boolean(a.last_entry?.blocker)));
}

/**
 * What waits for my partner, in «Что происходит» (Kostya's agent, 27.09: the line
 * «ждут Сани» must lead somewhere). My ideas are counted per partner and open
 * the list of my ideas awaiting a decision; results and requests are listed one
 * by one, each opening its task.
 */
export function waitingOnOthers(data: OverviewOut | undefined): {
  ideas: { name: string; count: number }[];
  items: WaitingItem[];
} {
  const ideas = new Map<string, number>();
  const items: WaitingItem[] = [];
  for (const w of data?.for_others ?? []) {
    if (w.kind === "idea_review") ideas.set(w.waiting_on.name, (ideas.get(w.waiting_on.name) ?? 0) + 1);
    else items.push(w);
  }
  return { ideas: [...ideas].map(([name, count]) => ({ name, count })), items };
}

/**
 * What waits for my decision, as cards. A code review is in the overview too (Spec 033, so an
 * agent's check sees it), but «Решить» draws it from its own query with the review's details:
 * left out here, it is neither drawn nor counted twice. My overdue tasks are cards of their
 * own, drawn after these (`myOverdue`), and merged code to release before them (`myReleases`).
 */
export function forMe(data: OverviewOut | undefined): WaitingItem[] {
  return (data?.for_me ?? []).filter((w) => w.kind !== "code_review" && w.kind !== "overdue" && w.kind !== "code_release");
}

/**
 * Spec 044: merged code of my task the server does not run yet — first, next to the code
 * reviews: «Выпустить» right in the card. It goes once the version is installed.
 */
export function myReleases(data: OverviewOut | undefined): WaitingItem[] {
  return (data?.for_me ?? []).filter((w) => w.kind === "code_release");
}

/**
 * Task d9165be7: my open tasks past their deadline, the most overdue first. Each stays until
 * the task is closed or its deadline moves on; my partner never sees mine.
 */
export function myOverdue(data: OverviewOut | undefined): WaitingItem[] {
  return (data?.for_me ?? []).filter((w) => w.kind === "overdue");
}

/**
 * Every card «Решить» shows: merged code to release, what waits for my decision, my overdue
 * tasks, what of mine is stuck, and the cases on review — one card «Кейсы на проверке: N»
 * however many there are (Spec 025, D4), so it counts once.
 */
export function waitingCount(data: OverviewOut | undefined): number {
  return myReleases(data).length + forMe(data).length + myOverdue(data).length + myBlocked(data).length + (casesInReview(data) > 0 ? 1 : 0);
}

/** Spec 025: the cases waiting for review, the same for both people; 0 from an older server. */
export function casesInReview(data: OverviewOut | undefined): number {
  return data?.cases_in_review ?? 0;
}

/** «1 идея», «3 идеи», «5 идей», «21 идея». */
export function ideasWord(n: number): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return "идея";
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return "идеи";
  return "идей";
}

/** A day of my own calendar, as a date field holds it: `yyyy-mm-dd`. */
export function localDay(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Task d9165be7: a moved deadline is the end of the chosen day in my own time zone. */
export function deadlineOf(day: string): string {
  return new Date(`${day}T23:59:59`).toISOString();
}
